import {
	AppIcon,
	IconButton,
	ReorderableList,
	Typography
} from "client/Components";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef
} from "react";
import { GlobalNavigationItemKind, GlobalNavigationPermissionsModel } from "client/Api/TitleBar/GlobalNavigation/globalNavigation";
import { GlobalNavigationGroupAndIndex, GlobalNavigationItemMenu } from "./GlobalNavigationItemMenu";
import { getReactColorAttributes } from "client_legacy/ndaw/appColor";
import { AppColorViewModel } from "client/Api/Apps/common";
import { GlobalNavigationItemContent } from "./GlobalNavigationItemContent";
import classNames from "classnames";
import styles from "./AppGlobalNavigationListItem.module.less";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import {
	ensureNotNil,
	isNil,
	setTrackingId
} from "client/Misc/utils";
import { DeleteConfirmationDialogData } from "./DeleteConfirmationDialog";
import { Guid } from "client_legacy/ndaw/guid";
import { useItemMenuState } from "client/Hooks";

export type AppGlobalNavigationListItemProps = {
	clientId: Guid;
	index: number;
	installedAppId: number;
	name: string;
	image: AppImageDisplayInfo;
	color: AppColorViewModel;
	permissions: GlobalNavigationPermissionsModel;
	onDelete(): void;
	onRemoveApp(installedAppId: number): void;
	onOpenDeleteConfirmation(data: DeleteConfirmationDialogData): Promise<boolean>;
	groups: readonly GlobalNavigationGroupAndIndex[];
	containingGroupIndex: number | null;
	onMoveToGroup(
		itemIndex: number,
		sourceGroupIndex: number | null,
		targetGroupIndex: number
	): void;
	onMoveOutOfGroup(
		itemIndex: number,
		sourceGroupIndex: number
	): void;
	appIdToScroll: Guid | null;
	onAppIdToScrollChange: (id: Guid | null) => void;
};

export const AppGlobalNavigationListItem = (props: AppGlobalNavigationListItemProps): ReactElement => {
	const {
		clientId,
		index,
		installedAppId,
		name,
		image,
		color,
		permissions,
		onDelete,
		onRemoveApp,
		onOpenDeleteConfirmation,
		groups,
		containingGroupIndex,
		onMoveOutOfGroup,
		onMoveToGroup,
		appIdToScroll,
		onAppIdToScrollChange
	} = props;

	const {
		menuState,
		onOpenMoreMenu,
		onOpenContextMenu,
		onCloseMenu
	} = useItemMenuState();

	const itemRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const moreButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const hasActions: boolean = permissions.canManageGlobalNavigation;
	const { colorClass, colorStyle } = getReactColorAttributes(color);

	const deleteHandler = useCallback(
		async (): Promise<void> => {
			if (!await onOpenDeleteConfirmation({ name })) {
				return;
			}

			onDelete();
			onRemoveApp(installedAppId);
		},
		[
			installedAppId,
			name,
			onDelete,
			onRemoveApp,
			onOpenDeleteConfirmation
		]
	);

	const moveToGroupHandler = useCallback(
		(targetGroupIndex: number): void => {
			onMoveToGroup(
				index,
				containingGroupIndex,
				targetGroupIndex
			);
		},
		[
			containingGroupIndex,
			index,
			onMoveToGroup
		]
	);

	const moveOutOfGroupHandler = useMemo(
		(): (() => void) | undefined => {
			if (isNil(containingGroupIndex)) {
				return undefined;
			}

			return (): void => {
				onMoveOutOfGroup(index, containingGroupIndex);
			};
		},
		[
			containingGroupIndex,
			index,
			onMoveOutOfGroup
		]
	);

	useEffect(
		(): void => {
			if (appIdToScroll && appIdToScroll === clientId) {
				itemRef.current
					?.scrollIntoView();

				onAppIdToScrollChange(null);
			}
		},
		[
			appIdToScroll,
			clientId,
			onAppIdToScrollChange
		]
	);

	return (
		<>
			<ReorderableList.RowItem
				trackingId="app-list-item"
				actions={
					hasActions
						? (
							<IconButton
								ref={moreButtonRef}
								onClick={() => onOpenMoreMenu()}
								icon="ellipsis"
								kind="transparent"
								trackingId="more"
								shape="round"
								isActive={menuState.isOpen && !menuState.isContextMenu}
							/>
						)
						: undefined
				}
				isActive={menuState.isOpen}
				mouseInteraction={{
					onContextMenu: hasActions
						? onOpenContextMenu
						: undefined
				}}
			>
				<GlobalNavigationItemContent
					icon={
						<span
							className={classNames(
								styles.appIcon,
								colorClass
							)}
							style={colorStyle}
						>
							<AppIcon value={image} color="inherit" />
						</span>
					}
				>
					<div
						{...setTrackingId("app-item-name")}
						ref={itemRef}
					>
						<Typography weight="bold">{name}</Typography>
					</div>
				</GlobalNavigationItemContent>
			</ReorderableList.RowItem>
			{hasActions && menuState.isOpen && (
				<GlobalNavigationItemMenu
					menuState={menuState}
					target={ensureNotNil(moreButtonRef.current)}
					onClose={onCloseMenu}
					kind={GlobalNavigationItemKind.InstalledApp}
					onDelete={deleteHandler}
					groups={groups}
					containingGroupIndex={containingGroupIndex}
					onMoveToGroup={moveToGroupHandler}
					onMoveOutOfGroup={moveOutOfGroupHandler}
					menuTitle={name}
				/>
			)}
		</>
	);
};

AppGlobalNavigationListItem.displayName = "AppGlobalNavigationListItem";
